import React, { useEffect, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Key, Users, FileText, Building2, Phone, Mail, Copy, Check, CreditCard } from 'lucide-react';
import PortalApplicationsView from '../components/PortalApplicationsView';

export default function MasterAgencyDashboard() {
  const { getAuthHeaders, user } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [escalationContacts, setEscalationContacts] = useState([]);
  const [copiedField, setCopiedField] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    password: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: ''
  });

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchData();
    fetchPaymentDetails();
    fetchEscalationContacts();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [agenciesRes, applicationsRes] = await Promise.all([
        axios.get(`${API_URL}/master-agency/agencies`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/master-agency/applications`, { headers: getAuthHeaders() })
      ]);
      setAgencies(agenciesRes.data);
      setApplications(applicationsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin-bank-details`, { headers: getAuthHeaders() });
      setPaymentDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch payment details');
    }
  };

  const fetchEscalationContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/escalation-contacts`, { headers: getAuthHeaders() });
      setEscalationContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch escalation contacts');
    }
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.success(`${fieldName} copied!`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleCreateAgency = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/master-agency/agencies`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('Agency created successfully');
      setIsAddDialogOpen(false);
      setFormData({ username: '', name: '', contact_person: '', email: '', phone: '', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agency');
    }
  };

  const handleEditAgency = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/master-agency/agencies/${selectedAgency.id}`, editFormData, {
        headers: getAuthHeaders()
      });
      toast.success('Agency updated successfully');
      setIsEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agency');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(
        `${API_URL}/master-agency/agencies/${selectedAgency.id}/reset-password`,
        { new_password: newPassword },
        { headers: getAuthHeaders() }
      );
      toast.success('Password reset successfully');
      setIsPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const openEditDialog = (agency) => {
    setSelectedAgency(agency);
    setEditFormData({
      name: agency.name || '',
      contact_person: agency.contact_person || '',
      email: agency.email || '',
      phone: agency.phone || ''
    });
    setIsEditDialogOpen(true);
  };

  const stats = {
    totalAgencies: agencies.length,
    activeAgencies: agencies.filter(a => a.is_active !== false).length,
    totalApplications: applications.length,
    pendingApplications: applications.filter(a => a.status === 'pending').length
  };

  return (
    <Sidebar>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Master Agency Dashboard
          </h1>
          <p className="text-slate-600 mt-2">Manage your agencies and view their applications</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg"><Users className="w-6 h-6 text-blue-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Total Agencies</p>
                <p className="text-2xl font-bold text-slate-900">{stats.totalAgencies}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg"><Building2 className="w-6 h-6 text-green-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Active Agencies</p>
                <p className="text-2xl font-bold text-slate-900">{stats.activeAgencies}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg"><FileText className="w-6 h-6 text-purple-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Total Applications</p>
                <p className="text-2xl font-bold text-slate-900">{stats.totalApplications}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg"><FileText className="w-6 h-6 text-yellow-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Pending Applications</p>
                <p className="text-2xl font-bold text-slate-900">{stats.pendingApplications}</p>
              </div>
            </div>
          </div>
        </div>

        {/* My Agencies Section */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm mb-8">
          <div className="flex justify-between items-center p-6 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900">My Agencies</h2>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-agency-btn" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                  <Plus className="w-4 h-4 mr-2" /> Add Agency
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Create New Agency</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateAgency} className="space-y-4">
                  <div><Label>Username *</Label><Input value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value})} required data-testid="new-agency-username" /></div>
                  <div><Label>Agency Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="new-agency-name" /></div>
                  <div><Label>Contact Person *</Label><Input value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} required /></div>
                  <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required /></div>
                  <div><Label>Phone *</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required /></div>
                  <div><Label>Password *</Label><Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required minLength={6} /></div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="create-agency-submit" className="bg-[#0052CC] text-white">Create Agency</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="agencies-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Contact</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Phone</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agencies.map((agency) => (
                    <tr key={agency.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="p-4 text-sm font-medium text-slate-700">{agency.name}</td>
                      <td className="p-4 text-sm text-slate-600">{agency.contact_person}</td>
                      <td className="p-4 text-sm text-slate-600">{agency.email}</td>
                      <td className="p-4 text-sm text-slate-600">{agency.phone}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${agency.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {agency.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => openEditDialog(agency)} title="Edit"><Edit className="w-4 h-4 text-blue-600" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => { setSelectedAgency(agency); setIsPasswordDialogOpen(true); }} title="Reset Password"><Key className="w-4 h-4 text-amber-600" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {agencies.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-slate-500">No agencies yet. Create your first agency to get started.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Portal Applications View with Filters - Replaces old Recent Applications Section */}
        <PortalApplicationsView />

        {/* Official Payment Details and Escalation Matrix */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          {/* Official Payment Details */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="master-agency-payment-details">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-[#0052CC]" />
              <h2 className="text-xl font-semibold text-slate-900">Official Payment Details</h2>
            </div>
            {paymentDetails && paymentDetails.account_holder_name ? (
              <div className="space-y-1">
                <div className="bg-purple-50 border border-purple-200 rounded p-2 mb-3">
                  <p className="text-xs text-purple-700 text-center">Click any field to copy</p>
                </div>
                
                <div 
                  className="group cursor-pointer hover:bg-slate-50 rounded p-2 transition-colors"
                  onClick={() => copyToClipboard(paymentDetails.account_holder_name, 'Account Holder')}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Account Holder</p>
                      <p className="text-sm text-slate-900 font-medium">{paymentDetails.account_holder_name}</p>
                    </div>
                    {copiedField === 'Account Holder' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                  </div>
                </div>
                
                <div 
                  className="group cursor-pointer hover:bg-slate-50 rounded p-2 transition-colors"
                  onClick={() => copyToClipboard(paymentDetails.account_number, 'Account Number')}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Account Number</p>
                      <p className="text-sm text-slate-900 font-medium">{paymentDetails.account_number}</p>
                    </div>
                    {copiedField === 'Account Number' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                  </div>
                </div>
                
                <div 
                  className="group cursor-pointer hover:bg-slate-50 rounded p-2 transition-colors"
                  onClick={() => copyToClipboard(paymentDetails.bank_name, 'Bank Name')}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Bank Name</p>
                      <p className="text-sm text-slate-900">{paymentDetails.bank_name}</p>
                    </div>
                    {copiedField === 'Bank Name' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div 
                    className="group cursor-pointer hover:bg-slate-50 rounded p-2 transition-colors"
                    onClick={() => copyToClipboard(paymentDetails.ifsc_code, 'IFSC Code')}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">IFSC Code</p>
                        <p className="text-sm text-slate-900">{paymentDetails.ifsc_code}</p>
                      </div>
                      {copiedField === 'IFSC Code' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                    </div>
                  </div>
                  <div 
                    className="group cursor-pointer hover:bg-slate-50 rounded p-2 transition-colors"
                    onClick={() => copyToClipboard(paymentDetails.branch, 'Branch')}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Branch</p>
                        <p className="text-sm text-slate-900">{paymentDetails.branch}</p>
                      </div>
                      {copiedField === 'Branch' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                    </div>
                  </div>
                </div>
                
                {paymentDetails.upi_id && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div 
                      className="group cursor-pointer hover:bg-emerald-50 rounded p-2 transition-colors"
                      onClick={() => copyToClipboard(paymentDetails.upi_id, 'UPI ID')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-emerald-600" />
                          <div>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">UPI ID</p>
                            <p className="text-sm text-emerald-700 font-medium">{paymentDetails.upi_id}</p>
                          </div>
                        </div>
                        {copiedField === 'UPI ID' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">No payment details available</p>
            )}
          </div>

          {/* Escalation Matrix */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="master-agency-escalation-matrix">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Escalation Matrix</h2>
            {escalationContacts.length > 0 ? (
              <div className="space-y-4">
                {escalationContacts.map((contact, index) => (
                  <div key={index} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{contact.level}</p>
                    <p className="text-sm font-medium text-slate-900">{contact.name}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-600">
                      <div className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        <span>{contact.mobile_number}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        <span>{contact.email}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">No escalation contacts available</p>
            )}
          </div>
        </div>

        {/* Edit Agency Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Edit Agency: {selectedAgency?.name}</DialogTitle></DialogHeader>
            <form onSubmit={handleEditAgency} className="space-y-4">
              <div><Label>Agency Name</Label><Input value={editFormData.name} onChange={(e) => setEditFormData({...editFormData, name: e.target.value})} /></div>
              <div><Label>Contact Person</Label><Input value={editFormData.contact_person} onChange={(e) => setEditFormData({...editFormData, contact_person: e.target.value})} /></div>
              <div><Label>Email</Label><Input type="email" value={editFormData.email} onChange={(e) => setEditFormData({...editFormData, email: e.target.value})} /></div>
              <div><Label>Phone</Label><Input value={editFormData.phone} onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})} /></div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#0052CC] text-white">Save Changes</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Password Reset Dialog */}
        <Dialog open={isPasswordDialogOpen} onOpenChange={(open) => { setIsPasswordDialogOpen(open); if (!open) setNewPassword(''); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Reset Password for {selectedAgency?.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              <div><Label>New Password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" data-testid="new-password-input" /></div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsPasswordDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleResetPassword} data-testid="reset-password-submit" className="bg-[#0052CC] text-white">Reset Password</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Sidebar>
  );
}
